import React from 'react'
import {
  buildVideoLayoutModel,
  listRailStates,
  listVideoLayouts,
  readStoredRailState,
  readStoredVideoLayout,
  writeStoredRailState,
  writeStoredVideoLayout,
} from '../layouts'
import {
  isParticipantVisibleInSurface,
  pickDominantParticipantForSurface,
} from '../domain'
import { RTC_PROVIDERS } from '../providerIds'
import { normalizeResolvedVideoCallQuality } from '../routing/videoCallQualityContract'
import { useRtcSessionHandleConfig } from '../shell/useRtcSessionHandleConfig'
import CallControlsOverlay from './CallControlsOverlay'
import VideoLayoutRenderer from './VideoLayoutRenderer'
import './videoLayout.css'

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeIdentifier(value, fallbackValue = null) {
  if (value === null || value === undefined) {
    return fallbackValue
  }

  return normalizeOptionalString(String(value), fallbackValue)
}

function getStorageKeys(sessionInterfaceId) {
  const normalizedSessionInterfaceId = normalizeOptionalString(
    sessionInterfaceId,
    'standard-live',
  )
  const storageVersion = 'v4'

  return {
    layout: `live.video-layout.${storageVersion}.${normalizedSessionInterfaceId}`,
    rail: `live.rail-state.${storageVersion}.${normalizedSessionInterfaceId}`,
  }
}

function resolveAllowedOptions(options = [], allowedIds = []) {
  if (!Array.isArray(allowedIds) || allowedIds.length === 0) {
    return options
  }

  return options.filter((option) => allowedIds.includes(option.id))
}

function createEmptyDeviceState() {
  return {
    cameraDevices: [],
    microphoneDevices: [],
    speakerDevices: [],
  }
}

function createMediaDeviceOption(deviceInfo, indexByKind) {
  const kind = normalizeOptionalString(deviceInfo?.kind, '')
  const deviceId = normalizeOptionalString(deviceInfo?.deviceId, '')
  if (!kind || !deviceId) {
    return null
  }

  const nextIndex = (indexByKind[kind] || 0) + 1
  indexByKind[kind] = nextIndex

  const fallbackLabelByKind = {
    audioinput: `Microphone ${nextIndex}`,
    videoinput: `Camera ${nextIndex}`,
    audiooutput: `Speaker ${nextIndex}`,
  }

  return {
    id: deviceId,
    label:
      normalizeOptionalString(deviceInfo?.label) ||
      fallbackLabelByKind[kind] ||
      `Device ${nextIndex}`,
  }
}

function normalizeDeviceCatalog(devices = []) {
  const indexByKind = {}
  const nextDeviceState = createEmptyDeviceState()

  devices.forEach((deviceInfo) => {
    const option = createMediaDeviceOption(deviceInfo, indexByKind)
    if (!option) {
      return
    }

    if (deviceInfo.kind === 'videoinput') {
      nextDeviceState.cameraDevices.push(option)
      return
    }

    if (deviceInfo.kind === 'audioinput') {
      nextDeviceState.microphoneDevices.push(option)
      return
    }

    if (deviceInfo.kind === 'audiooutput') {
      nextDeviceState.speakerDevices.push(option)
    }
  })

  return nextDeviceState
}

function readRtcConfig(sessionHandleConfig) {
  if (!sessionHandleConfig || typeof sessionHandleConfig !== 'object') {
    return {}
  }

  if (sessionHandleConfig.rtcProps && typeof sessionHandleConfig.rtcProps === 'object') {
    return sessionHandleConfig.rtcProps
  }

  return sessionHandleConfig
}

function hasBoolean(value) {
  return typeof value === 'boolean'
}

function parseLocalUsernameMetadata(username) {
  if (typeof username !== 'string' || !username.trim()) {
    return {
      displayName: null,
      profilePicId: null,
      role: null,
    }
  }

  const trimmedUsername = username.trim()

  try {
    const parsedUsername = JSON.parse(trimmedUsername)

    if (parsedUsername && typeof parsedUsername === 'object' && !Array.isArray(parsedUsername)) {
      return {
        displayName: normalizeOptionalString(
          parsedUsername?.name ?? parsedUsername?.displayName,
        ),
        profilePicId: normalizeOptionalString(parsedUsername?.profilePicId),
        role: normalizeOptionalString(parsedUsername?.role),
      }
    }
  } catch (_error) {
    return {
      displayName: trimmedUsername,
      profilePicId: null,
      role: null,
    }
  }

  return {
    displayName: trimmedUsername,
    profilePicId: null,
    role: null,
  }
}

function buildFallbackLocalParticipant({
  existingLocalParticipant = null,
  shellLocalParticipantId = null,
  rtcConfig = {},
  sessionHandleConfig = {},
  audioEnabled = true,
  videoEnabled = true,
} = {}) {
  if (existingLocalParticipant?.id) {
    return null
  }

  const localUsernameMetadata = parseLocalUsernameMetadata(
    sessionHandleConfig?.rtmProps?.username,
  )
  const fallbackId =
    normalizeOptionalString(shellLocalParticipantId) ||
    normalizeOptionalString(sessionHandleConfig?.localParticipantIdentity?.participantId) ||
    normalizeOptionalString(rtcConfig?.participantId) ||
    (rtcConfig?.uid !== undefined && rtcConfig?.uid !== null ? `local:${rtcConfig.uid}` : null) ||
    '__local__'

  return {
    id: fallbackId,
    participantId: fallbackId,
    displayName: localUsernameMetadata.displayName || 'You',
    profilePicId: localUsernameMetadata.profilePicId,
    role: localUsernameMetadata.role || 'participant',
    isLocal: true,
    connectionState: 'connected',
    audioMuted: audioEnabled !== true,
    videoMuted: videoEnabled !== true,
    screenSharing: false,
    videoHandle: null,
    renderKey: `${fallbackId}:placeholder`,
    transport: {
      uid:
        rtcConfig?.uid !== undefined && rtcConfig?.uid !== null
          ? String(rtcConfig.uid)
          : null,
    },
  }
}

function isSeparateLocalScreenShareParticipant(
  participant,
  { localRtcUid = null, localScreenShareUid = null } = {},
) {
  if (participant?.screenSharing !== true) {
    return false
  }

  const actorType = normalizeOptionalString(participant?.actorType)
  const actorId = normalizeOptionalString(participant?.actorId)
  const transportUid =
    normalizeOptionalString(participant?.transport?.uid) ||
    normalizeOptionalString(participant?.transport?.rtcUid)

  if (localScreenShareUid && transportUid === localScreenShareUid) {
    return true
  }

  return actorType === 'screen-share' && Boolean(localRtcUid) && actorId === localRtcUid
}

function mergeFallbackLocalParticipant(participants = [], fallbackLocalParticipant = null) {
  if (!fallbackLocalParticipant?.id) {
    return participants
  }

  if (
    participants.some(
      (participant) =>
        participant?.isLocal === true || participant?.id === fallbackLocalParticipant.id,
    )
  ) {
    return participants
  }

  return [...participants, fallbackLocalParticipant]
}

function isGenericParticipantName(displayName) {
  const normalizedDisplayName = normalizeOptionalString(displayName, '')

  return (
    !normalizedDisplayName ||
    /^participant(?:\s+\d+)?$/i.test(normalizedDisplayName)
  )
}

function isGenericParticipantRole(role) {
  const normalizedRole = normalizeOptionalString(role, '')

  return !normalizedRole || normalizedRole === 'participant'
}

function shouldApplyPeerParticipantHint(participant, peerParticipantHint) {
  if (!participant || participant?.isLocal === true || !peerParticipantHint) {
    return false
  }

  return (
    participant?.transport?.transportIdentityFallback === true ||
    isGenericParticipantName(participant?.displayName) ||
    isGenericParticipantRole(participant?.role) ||
    !normalizeOptionalString(participant?.profilePicId) ||
    !normalizeOptionalString(participant?.roleLabel)
  )
}

function applyPeerParticipantHint(participant, peerParticipantHint) {
  if (!shouldApplyPeerParticipantHint(participant, peerParticipantHint)) {
    return participant
  }

  return {
    ...participant,
    displayName: isGenericParticipantName(participant?.displayName)
      ? normalizeOptionalString(peerParticipantHint?.displayName, participant?.displayName)
      : participant?.displayName,
    profilePicId:
      normalizeOptionalString(participant?.profilePicId) ||
      normalizeOptionalString(peerParticipantHint?.profilePicId),
    role: isGenericParticipantRole(participant?.role)
      ? normalizeOptionalString(peerParticipantHint?.role, participant?.role)
      : participant?.role,
    roleLabel:
      normalizeOptionalString(participant?.roleLabel) ||
      normalizeOptionalString(peerParticipantHint?.roleLabel),
  }
}

function mergePeerParticipantHint(participants = [], peerParticipantHint = null) {
  if (!peerParticipantHint) {
    return participants
  }

  const remoteParticipants = participants.filter((participant) => participant?.isLocal !== true)

  if (remoteParticipants.length !== 1) {
    return participants
  }

  const targetParticipantId = remoteParticipants[0]?.id

  if (!targetParticipantId) {
    return participants
  }

  return participants.map((participant) =>
    participant?.id === targetParticipantId
      ? applyPeerParticipantHint(participant, peerParticipantHint)
      : participant,
  )
}

function canLocalParticipantShareScreen(participant) {
  const role = normalizeOptionalString(participant?.role, '')

  return ['mentor', 'doctor', 'therapist', 'practitioner'].includes(role)
}

export default function SharedVideoCallShell({
  shell,
  sessionInterfaceId,
  peerParticipantHint = null,
  handleLeaveRoom,
  layoutId: controlledLayoutId = null,
  railStateId: controlledRailStateId = null,
  onLayoutChange = null,
  onRailStateChange = null,
  showLayoutSelector = null,
  showRailSelector = null,
  showLeaveButton = true,
}) {
  const surfacePolicy = React.useMemo(() => shell?.surfacePolicy || {}, [shell?.surfacePolicy])
  const storageKeys = React.useMemo(
    () => getStorageKeys(sessionInterfaceId || shell?.sessionInterfaceId),
    [sessionInterfaceId, shell?.sessionInterfaceId],
  )
  const allowedVideoLayouts = React.useMemo(
    () =>
      resolveAllowedOptions(
        listVideoLayouts(),
        surfacePolicy?.allowedVideoLayoutIds,
      ),
    [surfacePolicy?.allowedVideoLayoutIds],
  )
  const allowedRailStates = React.useMemo(
    () =>
      resolveAllowedOptions(
        listRailStates(),
        surfacePolicy?.allowedRailStateIds,
      ),
    [surfacePolicy?.allowedRailStateIds],
  )
  const isLayoutControlled = Boolean(normalizeOptionalString(controlledLayoutId))
  const isRailStateControlled = Boolean(normalizeOptionalString(controlledRailStateId))
  const [uncontrolledLayoutId, setUncontrolledLayoutId] = React.useState(() =>
    readStoredVideoLayout(
      storageKeys.layout,
      surfacePolicy?.defaultVideoLayoutId,
    ),
  )
  const [uncontrolledRailStateId, setUncontrolledRailStateId] = React.useState(() =>
    readStoredRailState(
      storageKeys.rail,
      surfacePolicy?.defaultRailStateId,
    ),
  )
  const defaultLayoutId =
    surfacePolicy?.defaultVideoLayoutId || allowedVideoLayouts[0]?.id || 'stage-overlay'
  const fallbackLayoutId = allowedVideoLayouts[0]?.id || defaultLayoutId
  const controlledResolvedLayoutId =
    isLayoutControlled &&
    allowedVideoLayouts.some((option) => option.id === controlledLayoutId)
      ? controlledLayoutId
      : fallbackLayoutId
  const layoutId = isLayoutControlled ? controlledResolvedLayoutId : uncontrolledLayoutId
  const defaultRailStateId =
    surfacePolicy?.defaultRailStateId || allowedRailStates[0]?.id || 'stacked'
  const fallbackRailStateId = allowedRailStates[0]?.id || defaultRailStateId
  const controlledResolvedRailStateId =
    isRailStateControlled &&
    allowedRailStates.some((option) => option.id === controlledRailStateId)
      ? controlledRailStateId
      : fallbackRailStateId
  const railStateId = isRailStateControlled
    ? controlledResolvedRailStateId
    : uncontrolledRailStateId

  React.useEffect(() => {
    if (isLayoutControlled) {
      return
    }

    const nextLayoutId = readStoredVideoLayout(storageKeys.layout, defaultLayoutId)
    setUncontrolledLayoutId(nextLayoutId)
  }, [defaultLayoutId, isLayoutControlled, storageKeys.layout])

  React.useEffect(() => {
    if (isRailStateControlled) {
      return
    }

    setUncontrolledRailStateId(readStoredRailState(storageKeys.rail, defaultRailStateId))
  }, [defaultRailStateId, isRailStateControlled, storageKeys.rail])

  React.useEffect(() => {
    if (isLayoutControlled) {
      return
    }

    if (allowedVideoLayouts.some((option) => option.id === layoutId)) {
      return
    }

    setUncontrolledLayoutId(fallbackLayoutId)
    writeStoredVideoLayout(storageKeys.layout, fallbackLayoutId)
  }, [allowedVideoLayouts, fallbackLayoutId, isLayoutControlled, layoutId, storageKeys.layout])

  React.useEffect(() => {
    if (isRailStateControlled) {
      return
    }

    if (allowedRailStates.some((option) => option.id === railStateId)) {
      return
    }

    setUncontrolledRailStateId(fallbackRailStateId)
    writeStoredRailState(storageKeys.rail, fallbackRailStateId)
  }, [allowedRailStates, fallbackRailStateId, isRailStateControlled, railStateId, storageKeys.rail])

  const shellParticipants = React.useMemo(
    () => (Array.isArray(shell?.participants) ? shell.participants : []),
    [shell?.participants],
  )
  const sessionHandleConfig = useRtcSessionHandleConfig(shell?.sessionHandle)
  const rtcConfig = React.useMemo(
    () => readRtcConfig(sessionHandleConfig),
    [sessionHandleConfig],
  )
  const shellVisibleParticipants = React.useMemo(
    () => (Array.isArray(shell?.visibleParticipants) ? shell.visibleParticipants : []),
    [shell?.visibleParticipants],
  )
  const shellLocalParticipant =
    shellParticipants.find((participant) => participant?.isLocal === true) || null
  const localRtcUid = normalizeIdentifier(rtcConfig?.uid)
  const localScreenShareUid = normalizeIdentifier(rtcConfig?.screenshareUid)
  const separateLocalScreenShareParticipant = React.useMemo(
    () =>
      shellParticipants.find((participant) =>
        isSeparateLocalScreenShareParticipant(participant, {
          localRtcUid,
          localScreenShareUid,
        }),
      ) || null,
    [localRtcUid, localScreenShareUid, shellParticipants],
  )
  const derivedLocalScreenShareActive =
    shellLocalParticipant?.screenSharing === true ||
    Boolean(separateLocalScreenShareParticipant)
  const [optimisticLocalMediaState, setOptimisticLocalMediaState] = React.useState(() => ({
    audioEnabled: hasBoolean(rtcConfig?.enableAudio)
      ? rtcConfig.enableAudio
      : shellLocalParticipant?.audioMuted !== true,
    videoEnabled: hasBoolean(rtcConfig?.enableVideo)
      ? rtcConfig.enableVideo
      : shellLocalParticipant?.videoMuted !== true,
    screenShareActive: derivedLocalScreenShareActive,
  }))
  const hasHydratedLocalMediaStateRef = React.useRef(false)

  React.useEffect(() => {
    if (!hasHydratedLocalMediaStateRef.current) {
      hasHydratedLocalMediaStateRef.current = true
      return
    }

    if (
      !shellLocalParticipant?.id &&
      shellLocalParticipant?.audioMuted === undefined &&
      shellLocalParticipant?.videoMuted === undefined &&
      shellLocalParticipant?.screenSharing === undefined &&
      !separateLocalScreenShareParticipant?.id &&
      !hasBoolean(rtcConfig?.enableAudio) &&
      !hasBoolean(rtcConfig?.enableVideo)
    ) {
      return
    }

    setOptimisticLocalMediaState((previousState) => ({
      audioEnabled: hasBoolean(rtcConfig?.enableAudio)
        ? rtcConfig.enableAudio
        : shellLocalParticipant?.audioMuted !== undefined
          ? shellLocalParticipant.audioMuted !== true
          : previousState.audioEnabled,
      videoEnabled: hasBoolean(rtcConfig?.enableVideo)
        ? rtcConfig.enableVideo
        : shellLocalParticipant?.videoMuted !== undefined
          ? shellLocalParticipant.videoMuted !== true
          : previousState.videoEnabled,
      screenShareActive: derivedLocalScreenShareActive,
    }))
  }, [
    derivedLocalScreenShareActive,
    rtcConfig?.enableAudio,
    rtcConfig?.enableVideo,
    separateLocalScreenShareParticipant?.id,
    shellLocalParticipant?.audioMuted,
    shellLocalParticipant?.id,
    shellLocalParticipant?.screenSharing,
    shellLocalParticipant?.videoMuted,
  ])

  const resolvedAudioEnabled = optimisticLocalMediaState.audioEnabled
  const resolvedVideoEnabled = optimisticLocalMediaState.videoEnabled
  const fallbackLocalParticipant = React.useMemo(
    () =>
      buildFallbackLocalParticipant({
        existingLocalParticipant: shellLocalParticipant,
        shellLocalParticipantId: shell?.localParticipantId,
        rtcConfig,
        sessionHandleConfig,
        audioEnabled: resolvedAudioEnabled,
        videoEnabled: resolvedVideoEnabled,
      }),
    [
      resolvedAudioEnabled,
      resolvedVideoEnabled,
      rtcConfig,
      sessionHandleConfig,
      shell?.localParticipantId,
      shellLocalParticipant,
    ],
  )
  const participants = React.useMemo(
    () =>
      mergePeerParticipantHint(
        mergeFallbackLocalParticipant(shellParticipants, fallbackLocalParticipant),
        peerParticipantHint,
      ),
    [fallbackLocalParticipant, peerParticipantHint, shellParticipants],
  )
  const visibleParticipants = React.useMemo(
    () =>
      mergePeerParticipantHint(
        mergeFallbackLocalParticipant(shellVisibleParticipants, fallbackLocalParticipant),
        peerParticipantHint,
      ),
    [fallbackLocalParticipant, peerParticipantHint, shellVisibleParticipants],
  )
  const localParticipant =
    participants.find((participant) => participant?.isLocal === true) || fallbackLocalParticipant
  const localScreenShareActive = optimisticLocalMediaState.screenShareActive === true
  const projectScreenShareOntoLocalParticipant =
    localScreenShareActive && !separateLocalScreenShareParticipant
  const presentationParticipants = React.useMemo(
    () =>
      participants.map((participant) => {
        if (participant?.isLocal !== true) {
          return participant
        }

        return {
          ...participant,
          audioMuted: !resolvedAudioEnabled,
          videoMuted: !resolvedVideoEnabled,
          screenSharing: projectScreenShareOntoLocalParticipant,
        }
      }),
    [
      participants,
      projectScreenShareOntoLocalParticipant,
      resolvedAudioEnabled,
      resolvedVideoEnabled,
    ],
  )
  const presentationVisibleParticipants = React.useMemo(
    () =>
      visibleParticipants.map((participant) => {
        if (participant?.isLocal !== true) {
          return participant
        }

        return {
          ...participant,
          audioMuted: !resolvedAudioEnabled,
          videoMuted: !resolvedVideoEnabled,
          screenSharing: projectScreenShareOntoLocalParticipant,
        }
      }),
    [
      projectScreenShareOntoLocalParticipant,
      resolvedAudioEnabled,
      resolvedVideoEnabled,
      visibleParticipants,
    ],
  )
  const renderedParticipants = React.useMemo(
    () =>
      presentationParticipants.filter((participant) =>
        isParticipantVisibleInSurface(
          sessionInterfaceId || shell?.sessionInterfaceId,
          participant,
        ),
      ),
    [presentationParticipants, sessionInterfaceId, shell?.sessionInterfaceId],
  )
  const renderedVisibleParticipants = React.useMemo(
    () =>
      presentationVisibleParticipants.filter((participant) =>
        isParticipantVisibleInSurface(
          sessionInterfaceId || shell?.sessionInterfaceId,
          participant,
        ),
      ),
    [
      presentationVisibleParticipants,
      sessionInterfaceId,
      shell?.sessionInterfaceId,
    ],
  )
  const effectiveDominantParticipantId = React.useMemo(
    () =>
      pickDominantParticipantForSurface(
        sessionInterfaceId || shell?.sessionInterfaceId,
        renderedVisibleParticipants,
        shell?.manualDominantParticipantId,
      )?.id ||
      shell?.dominantParticipantId ||
      null,
    [
      renderedVisibleParticipants,
      sessionInterfaceId,
      shell?.dominantParticipantId,
      shell?.manualDominantParticipantId,
      shell?.sessionInterfaceId,
    ],
  )
  const layoutModel = React.useMemo(
    () =>
      buildVideoLayoutModel({
        layoutId,
        railStateId,
        participants: renderedParticipants,
        visibleParticipants: renderedVisibleParticipants,
        dominantParticipantId: effectiveDominantParticipantId,
        surfacePolicy,
        localParticipantId: localParticipant?.id || shell?.localParticipantId || null,
      }),
    [
        layoutId,
        railStateId,
        effectiveDominantParticipantId,
        renderedParticipants,
        renderedVisibleParticipants,
        shell?.localParticipantId,
        localParticipant?.id,
        surfacePolicy,
    ],
  )
  const availableRailStates = React.useMemo(
    () =>
      resolveAllowedOptions(
        listRailStates(),
        Array.isArray(layoutModel?.availableRailStateIds) &&
          layoutModel.availableRailStateIds.length
          ? layoutModel.availableRailStateIds
          : surfacePolicy?.allowedRailStateIds,
      ),
    [layoutModel?.availableRailStateIds, surfacePolicy?.allowedRailStateIds],
  )
  const resolvedShowLayoutSelector =
    typeof showLayoutSelector === 'boolean'
      ? showLayoutSelector
      : allowedVideoLayouts.length > 1
  const resolvedShowRailSelector =
    typeof showRailSelector === 'boolean'
      ? showRailSelector
      : layoutModel.hasRail && availableRailStates.length > 1
  const debugContext = React.useMemo(
    () => ({
      providerId: normalizeOptionalString(shell?.providerId),
      callPhase: normalizeOptionalString(shell?.callPhase, 'idle'),
      sessionInterfaceId:
        normalizeOptionalString(sessionInterfaceId) ||
        normalizeOptionalString(shell?.sessionInterfaceId),
      layoutId,
      railStateId: layoutModel?.railStateId || railStateId,
      videoQuality: normalizeResolvedVideoCallQuality(rtcConfig?.videoQuality),
    }),
    [
      layoutModel?.railStateId,
      layoutId,
      railStateId,
      rtcConfig?.videoQuality,
      sessionInterfaceId,
      shell?.callPhase,
      shell?.providerId,
      shell?.sessionInterfaceId,
    ],
  )
  const allowManualFocus = surfacePolicy?.allowManualFocus !== false
  const showAudioButton = surfacePolicy?.allowAudioControl !== false
  const showVideoButton = surfacePolicy?.allowVideoControl !== false
  const showDeviceSettingsButton =
    surfacePolicy?.allowDeviceSettingsControl !== false &&
    typeof shell?.rtcSession?.setDevices === 'function'
  const showScreenShareButton =
    surfacePolicy?.allowScreenShare === true &&
    canLocalParticipantShareScreen(localParticipant) &&
    typeof shell?.rtcSession?.startScreenShare === 'function' &&
    typeof shell?.rtcSession?.stopScreenShare === 'function'
  const screenShareRuntimeReady =
    shell?.providerId !== RTC_PROVIDERS.agora ||
    shell?.runtimeCapabilities?.screenShare === true ||
    shell?.callPhase === 'joined'
  const canToggleScreenShare =
    showScreenShareButton &&
    (localScreenShareActive === true ||
      screenShareRuntimeReady)
  const [openPanel, setOpenPanel] = React.useState(null)
  const [deviceCatalog, setDeviceCatalog] = React.useState(createEmptyDeviceState)
  const [screenShareRequestPending, setScreenShareRequestPending] = React.useState(false)
  const deviceSettingsOpen = openPanel === 'devices'
  const layoutMenuOpen = openPanel === 'layout'

  React.useEffect(() => {
    const mediaDevices =
      typeof navigator !== 'undefined' ? navigator.mediaDevices : null

    if (!mediaDevices?.enumerateDevices) {
      setDeviceCatalog(createEmptyDeviceState())
      return undefined
    }

    let cancelled = false
    const refreshDevices = async () => {
      try {
        const nextDevices = await mediaDevices.enumerateDevices()
        if (!cancelled) {
          setDeviceCatalog(normalizeDeviceCatalog(nextDevices))
        }
      } catch (error) {
        if (!cancelled) {
          setDeviceCatalog(createEmptyDeviceState())
        }
      }
    }

    refreshDevices()

    if (typeof mediaDevices.addEventListener !== 'function') {
      return () => {
        cancelled = true
      }
    }

    const handleDeviceChange = () => {
      refreshDevices()
    }

    mediaDevices.addEventListener('devicechange', handleDeviceChange)

    return () => {
      cancelled = true
      mediaDevices.removeEventListener('devicechange', handleDeviceChange)
    }
  }, [])

  const handleLayoutChange = React.useCallback(
    (nextLayoutId) => {
      if (!isLayoutControlled) {
        setUncontrolledLayoutId(nextLayoutId)
      }
      writeStoredVideoLayout(storageKeys.layout, nextLayoutId)
      setOpenPanel(null)
      onLayoutChange?.(nextLayoutId)
    },
    [isLayoutControlled, onLayoutChange, storageKeys.layout],
  )
  const handleRailStateChange = React.useCallback(
    (nextRailStateId) => {
      if (!isRailStateControlled) {
        setUncontrolledRailStateId(nextRailStateId)
      }
      writeStoredRailState(storageKeys.rail, nextRailStateId)
      setOpenPanel(null)
      onRailStateChange?.(nextRailStateId)
    },
    [isRailStateControlled, onRailStateChange, storageKeys.rail],
  )
  const handleSelectParticipant = React.useCallback(
    (participant) => {
      if (
        !participant?.id ||
        layoutId === 'grid' ||
        !allowManualFocus
      ) {
        return
      }

      shell?.setPreferredDominantParticipantId?.(participant.id)
    },
    [allowManualFocus, layoutId, shell],
  )
  const handleExpandRail = React.useCallback(() => {
    const expandedRailStateId =
      allowedRailStates.find((option) => option.id !== 'collapsed')?.id || 'stacked'
    handleRailStateChange(expandedRailStateId)
  }, [allowedRailStates, handleRailStateChange])
  const handleToggleAudio = React.useCallback(() => {
    if (!shell?.rtcSession || !showAudioButton) {
      return
    }

    const nextAudioEnabled = !resolvedAudioEnabled
    setOptimisticLocalMediaState((previousState) => ({
      ...previousState,
      audioEnabled: nextAudioEnabled,
    }))

    shell.rtcSession
      .setLocalAudioEnabled(nextAudioEnabled)
      .catch(() => {
        setOptimisticLocalMediaState((previousState) => ({
          ...previousState,
          audioEnabled: resolvedAudioEnabled,
        }))
      })
  }, [resolvedAudioEnabled, shell, showAudioButton])
  const handleToggleVideo = React.useCallback(() => {
    if (!shell?.rtcSession || !showVideoButton) {
      return
    }

    const nextVideoEnabled = !resolvedVideoEnabled
    setOptimisticLocalMediaState((previousState) => ({
      ...previousState,
      videoEnabled: nextVideoEnabled,
    }))

    shell.rtcSession
      .setLocalVideoEnabled(nextVideoEnabled)
      .catch(() => {
        setOptimisticLocalMediaState((previousState) => ({
          ...previousState,
          videoEnabled: resolvedVideoEnabled,
        }))
      })
  }, [resolvedVideoEnabled, shell, showVideoButton])
  const handleLeave = React.useCallback(() => {
    Promise.resolve(shell?.rtcSession?.leave?.())
      .catch(() => {})
      .finally(() => {
        handleLeaveRoom?.()
      })
  }, [handleLeaveRoom, shell?.rtcSession])
  const handleToggleScreenShare = React.useCallback(() => {
    if (!canToggleScreenShare || !shell?.rtcSession || screenShareRequestPending) {
      return
    }

    const nextScreenShareActive = !localScreenShareActive
    const action = nextScreenShareActive
      ? shell.rtcSession.startScreenShare
      : shell.rtcSession.stopScreenShare

    setOptimisticLocalMediaState((previousState) => ({
      ...previousState,
      screenShareActive: nextScreenShareActive,
    }))
    setScreenShareRequestPending(true)
    Promise.resolve(action?.())
      .catch((error) => {
        setOptimisticLocalMediaState((previousState) => ({
          ...previousState,
          screenShareActive: localScreenShareActive,
        }))
        console.warn('[SharedVideoCallShell] Failed to toggle screen share', error)
      })
      .finally(() => {
        setScreenShareRequestPending(false)
      })
  }, [canToggleScreenShare, localScreenShareActive, screenShareRequestPending, shell])
  const handleToggleLayoutMenu = React.useCallback(() => {
    setOpenPanel((previousPanel) => (previousPanel === 'layout' ? null : 'layout'))
  }, [])
  const handleToggleDeviceSettings = React.useCallback(() => {
    setOpenPanel((previousPanel) => (previousPanel === 'devices' ? null : 'devices'))
  }, [])
  const handleSelectDevice = React.useCallback(
    (deviceKey, deviceId) => {
      if (!shell?.rtcSession?.setDevices) {
        return
      }

      shell.rtcSession.setDevices({
        [deviceKey]: deviceId,
      }).catch(() => {})
    },
    [shell?.rtcSession],
  )
  const participantSelectHandler = allowManualFocus ? handleSelectParticipant : null

  React.useEffect(() => {
    if ((resolvedShowLayoutSelector || resolvedShowRailSelector) || openPanel !== 'layout') {
      return
    }

    setOpenPanel(null)
  }, [openPanel, resolvedShowLayoutSelector, resolvedShowRailSelector])

  React.useEffect(() => {
    if (showDeviceSettingsButton || openPanel !== 'devices') {
      return
    }

    setOpenPanel(null)
  }, [openPanel, showDeviceSettingsButton])

  return (
    <div className="shared-video-call-shell">
      <VideoLayoutRenderer
        participants={renderedParticipants}
        layoutModel={layoutModel}
        videoBinder={shell?.videoBinder}
        onSelectParticipant={participantSelectHandler}
        onExpandRail={handleExpandRail}
        debugContext={debugContext}
      />

      <CallControlsOverlay
        audioEnabled={resolvedAudioEnabled}
        videoEnabled={resolvedVideoEnabled}
        layoutId={layoutId}
        railStateId={layoutModel.railStateId}
        videoLayoutOptions={allowedVideoLayouts}
        railStateOptions={availableRailStates}
        onLayoutChange={handleLayoutChange}
        onRailStateChange={handleRailStateChange}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
        onLeave={handleLeave}
        screenShareActive={localScreenShareActive}
        onToggleScreenShare={handleToggleScreenShare}
        showScreenShareButton={showScreenShareButton}
        disableScreenShareButton={!canToggleScreenShare || screenShareRequestPending}
        layoutMenuOpen={layoutMenuOpen}
        onToggleLayoutMenu={handleToggleLayoutMenu}
        deviceSettingsOpen={deviceSettingsOpen}
        onToggleDeviceSettings={handleToggleDeviceSettings}
        showDeviceSettingsButton={showDeviceSettingsButton}
        cameraDevices={deviceCatalog.cameraDevices}
        microphoneDevices={deviceCatalog.microphoneDevices}
        speakerDevices={deviceCatalog.speakerDevices}
        selectedCameraDeviceId={rtcConfig?.cameraDeviceId || ''}
        selectedMicrophoneDeviceId={rtcConfig?.microphoneDeviceId || ''}
        selectedSpeakerDeviceId={rtcConfig?.playbackDeviceId || ''}
        onCameraDeviceChange={(deviceId) => {
          handleSelectDevice('cameraDeviceId', deviceId)
        }}
        onMicrophoneDeviceChange={(deviceId) => {
          handleSelectDevice('microphoneDeviceId', deviceId)
        }}
        onSpeakerDeviceChange={(deviceId) => {
          handleSelectDevice('playbackDeviceId', deviceId)
        }}
        showLayoutSelector={resolvedShowLayoutSelector}
        showRailSelector={resolvedShowRailSelector}
        showAudioButton={showAudioButton}
        showVideoButton={showVideoButton}
        showLeaveButton={showLeaveButton}
      />
    </div>
  )
}

SharedVideoCallShell.displayName = 'SharedVideoCallShell'
